import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Attachment, Message, MessageListResponse } from '@chatapp-inbox/shared';
import { messagesKey } from '../conversation';

/** Formato do cache de mensagens (a T26 aplica eventos do tempo real sobre ele). */
export type MessagePages = InfiniteData<MessageListResponse, string | null>;

/**
 * Mensagens em ordem cronológica (a mais antiga primeiro). A página 0 do cache é a mais recente e
 * cada página já vem em ordem cronológica, então as páginas entram da mais antiga para a mais
 * nova. Repetidas por id (uma página nova pode repetir a ponta da vizinha) ficam só uma vez.
 */
export function flattenMessages(data: MessagePages | undefined): Message[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: Message[] = [];
  for (let i = data.pages.length - 1; i >= 0; i--) {
    for (const message of data.pages[i]?.messages ?? []) {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
      out.push(message);
    }
  }
  return out;
}

const timeOf = (message: Pick<Message, 'createdAt'>): number => Date.parse(message.createdAt);

/**
 * Grava no cache de mensagens SEM mexer na idade dos dados. `setQueryData` carimba "agora" como o
 * momento da última busca, e então o histórico passaria por recém-atualizado: abrir a conversa
 * não pediria nada ao servidor (a conversa mostraria o que havia horas atrás) e o refetch do foco
 * da janela esperaria o prazo de novo. Quem escreve aqui só acrescenta o que já sabe; a "idade" é
 * a da última busca de verdade. O atualizador devolve `undefined` quando não há o que mudar: aí
 * nada é gravado.
 */
function writePages(
  queryClient: QueryClient,
  conversationId: string,
  update: (cached: MessagePages | undefined) => MessagePages | undefined,
): void {
  const key = messagesKey(conversationId);
  const updatedAt = queryClient.getQueryState(key)?.dataUpdatedAt;
  queryClient.setQueryData<MessagePages>(key, update, updatedAt ? { updatedAt } : undefined);
}

/** Põe a mensagem na posição cronológica de uma lista já ordenada (depois das de mesmo horário). */
function insertByTime(messages: readonly Message[], message: Message): Message[] {
  const result = [...messages];
  let index = result.length;
  while (index > 0 && timeOf(result[index - 1]!) > timeOf(message)) index--;
  result.splice(index, 0, message);
  return result;
}

/**
 * Coloca `message` na página mais recente, na posição cronológica. Não faz nada se o cache de
 * mensagens não existir (não o cria: depois de um logout isso poria dado de outra sessão no cache
 * da pessoa seguinte) ou se a mensagem já estiver lá (um refetch pode tê-la trazido antes da
 * resposta do envio, e a cópia do cache pode ser mais nova que a da resposta). Devolve se o cache
 * já tem a mensagem ao final.
 */
export function addMessageToCache(
  queryClient: QueryClient,
  conversationId: string,
  message: Message,
): boolean {
  let present = false;
  writePages(queryClient, conversationId, (cached) => {
    const latest = cached?.pages[0];
    if (!cached || !latest) return undefined;
    present = true;
    if (cached.pages.some((page) => page.messages.some((m) => m.id === message.id))) {
      return undefined;
    }
    const messages = insertByTime(latest.messages, message);
    return { ...cached, pages: [{ ...latest, messages }, ...cached.pages.slice(1)] };
  });
  return present;
}

/**
 * Troca no cache a mensagem de mesmo id pela versão do servidor (resposta do reenvio); se ela não
 * estiver no cache, entra como uma mensagem nova.
 *
 * O reenvio é uma tentativa NOVA: o servidor passa o horário da mensagem para "agora" e, na linha do
 * tempo dele, ela vai para o FIM da conversa. Trocar no mesmo lugar deixaria no meio do histórico de
 * ontem uma mensagem das 13:40 (e o separador "Hoje" no lugar errado) até o próximo refetch: quando
 * o horário mudou, a mensagem sai do lugar antigo e entra na posição cronológica da página mais
 * recente.
 */
export function replaceMessageInCache(
  queryClient: QueryClient,
  conversationId: string,
  message: Message,
): void {
  let found = false;
  writePages(queryClient, conversationId, (cached) => {
    if (!cached) return undefined;
    const previous = cached.pages.flatMap((page) => page.messages).find((m) => m.id === message.id);
    if (!previous) return undefined;
    found = true;
    if (previous.createdAt === message.createdAt) {
      return {
        ...cached,
        pages: cached.pages.map((page) =>
          page.messages.some((m) => m.id === message.id)
            ? { ...page, messages: page.messages.map((m) => (m.id === message.id ? message : m)) }
            : page,
        ),
      };
    }
    const pages = cached.pages.map((page) =>
      page.messages.some((m) => m.id === message.id)
        ? { ...page, messages: page.messages.filter((m) => m.id !== message.id) }
        : page,
    );
    const latest = pages[0];
    if (!latest) return undefined;
    return {
      ...cached,
      pages: [{ ...latest, messages: insertByTime(latest.messages, message) }, ...pages.slice(1)],
    };
  });
  if (!found) addMessageToCache(queryClient, conversationId, message);
}

/**
 * Deixa só a página mais recente no cache de uma conversa que está sendo ABERTA. Ao reabrir, o
 * React Query refaz TODAS as páginas guardadas, uma depois da outra; quem lia o histórico há
 * minutos não precisa de 4 requisições em série para ver o fim da conversa. As páginas mais
 * antigas voltam sob demanda.
 */
export function trimToLatestPage(queryClient: QueryClient, conversationId: string): void {
  writePages(queryClient, conversationId, (cached) => {
    if (!cached || cached.pages.length <= 1) return undefined;
    return { pages: cached.pages.slice(0, 1), pageParams: cached.pageParams.slice(0, 1) };
  });
}

/**
 * Muda o estado de envio de uma mensagem já no cache (evento `message.status` do tempo real), no
 * mesmo lugar e sem criar nada. Devolve se a mensagem estava no cache.
 */
export function patchMessageStatusInCache(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
  patch: Pick<Message, 'status' | 'errorCode' | 'errorDetail'>,
): boolean {
  let found = false;
  writePages(queryClient, conversationId, (cached) => {
    if (!cached) return undefined;
    const holder = cached.pages.find((page) => page.messages.some((m) => m.id === messageId));
    if (!holder) return undefined;
    found = true;
    const current = holder.messages.find((m) => m.id === messageId)!;
    if (
      current.status === patch.status &&
      current.errorCode === patch.errorCode &&
      (current.errorDetail ?? null) === (patch.errorDetail ?? null)
    ) {
      return undefined;
    }
    return {
      ...cached,
      pages: cached.pages.map((page) =>
        page === holder
          ? {
              ...page,
              messages: page.messages.map((m) => (m.id === messageId ? { ...m, ...patch } : m)),
            }
          : page,
      ),
    };
  });
  return found;
}

/**
 * Atualiza o anexo de uma mensagem já no cache (evento `attachment.updated`: o arquivo recebido
 * terminou de baixar ou falhou), no mesmo lugar e sem criar nada. Devolve se a mensagem estava no cache.
 */
export function patchAttachmentInCache(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
  attachment: Attachment,
): boolean {
  let found = false;
  writePages(queryClient, conversationId, (cached) => {
    if (!cached) return undefined;
    const holder = cached.pages.find((page) => page.messages.some((m) => m.id === messageId));
    if (!holder) return undefined;
    found = true;
    const current = holder.messages.find((m) => m.id === messageId)!;
    if (JSON.stringify(current.attachment) === JSON.stringify(attachment)) return undefined;
    return {
      ...cached,
      pages: cached.pages.map((page) =>
        page === holder
          ? {
              ...page,
              messages: page.messages.map((m) => (m.id === messageId ? { ...m, attachment } : m)),
            }
          : page,
      ),
    };
  });
  return found;
}
